// CSR-794 — Shared formatter for the COI Description of Operations vehicles
// section. Used by both the server (`server/services/coiTemplate.ts`) and
// the client (`client/src/components/insured/SendCOIDialog.tsx`) so the
// modal-driven and template-driven COI flows produce identical lines.
//
// Format (per the COI Field Mapping doc, section 8 + rule R3):
//   Year, Make, Model, VIN: <vin>, ($Value), Deductible (Collision): $X,XXX, Deductible (Comprehensive): $X,XXX
// Optional segments are omitted (no placeholders) when the field is absent
// per rule R7.

import type { PolicyCoverages, PolicyVehicle } from "./schema";

export function isNonOwnedAttachedTrailer(v: PolicyVehicle): boolean {
  // CSR-677: NOA records arrive from upstream in many partial shapes —
  // legacy NowCerts only sets `truckType`, the logrock-dc sanitizer
  // collapses to `make`, un-sanitized Progressive splits across
  // `make`/`model`, and degraded NowCerts rows have just `make: "Non-Owned"`.
  // Substring match across all three fields catches every observed shape;
  // "non-owned" is not a real manufacturer or vehicle type, so a false
  // positive is unrealistic.
  const fields = [(v as { truckType?: string }).truckType, v.make, v.model];
  return fields.some(
    (s) => typeof s === "string" && s.trim().toLowerCase().includes("non-owned"),
  );
}

// Top-level Progressive sync fields take precedence over the legacy NowCerts
// `vehicleSpecificCoverages` shape; for that shape we look up by `name`
// (case-insensitive) and fall back to `typeCode` (COMP / COLL).
function pickPdNumber(
  v: PolicyVehicle,
  topKey: "value" | "deductibleComp" | "deductibleCollision",
  covName: "comprehensive" | "collision",
  covKey: "limits" | "deductibles",
): number | null {
  const top = (v as Record<string, unknown>)[topKey];
  const fromTop =
    typeof top === "string"
      ? parseFloat(top.replace(/[^\d.-]/g, ""))
      : typeof top === "number"
        ? top
        : NaN;
  if (Number.isFinite(fromTop) && fromTop > 0) return fromTop;
  const cov = (v as { vehicleSpecificCoverages?: Array<{ name?: string; typeCode?: string; limits?: unknown[]; deductibles?: unknown[] }> }).vehicleSpecificCoverages;
  if (!Array.isArray(cov)) return null;
  const typeCode = covName === "comprehensive" ? "COMP" : "COLL";
  const entry = cov.find((c) => {
    const name = (c?.name || "").toLowerCase();
    if (name === covName) return true;
    return (c?.typeCode || "").toUpperCase() === typeCode;
  });
  const arr = entry?.[covKey];
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const first = arr[0];
  return typeof first === "number" && Number.isFinite(first) && first > 0 ? first : null;
}

// True iff the vehicle carries a non-zero comprehensive or collision deductible
// under either the Progressive top-level shape or the legacy NowCerts
// `vehicleSpecificCoverages` shape. Reused by the Insured > Physical Damage
// card so a NowCerts-synced vehicle is counted as covered the same way the
// COI Description of Operations renders it.
export function hasPdDeductible(v: PolicyVehicle): boolean {
  return (
    pickPdNumber(v, "deductibleComp", "comprehensive", "deductibles") !== null ||
    pickPdNumber(v, "deductibleCollision", "collision", "deductibles") !== null
  );
}

// CSR-953 — read a vehicle's (comprehensive, collision) deductible pair using
// the same `pickPdNumber` source `formatVehicleCoiLine` / `hasPdDeductible`
// use, so the deductible-tier split never disagrees with the issued
// certificate. Returns `null` for a deductible that is absent or non-positive.
export function getVehicleDeductiblePair(v: PolicyVehicle): {
  comprehensive: number | null;
  collision: number | null;
} {
  return {
    comprehensive: pickPdNumber(v, "deductibleComp", "comprehensive", "deductibles"),
    collision: pickPdNumber(v, "deductibleCollision", "collision", "deductibles"),
  };
}

function fmtAmount(n: number): string {
  return `$${n.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

function parseDeductibleAmount(raw: string | null | undefined): number | null {
  if (raw == null) return null;
  const cleaned = String(raw).replace(/[^\d.-]/g, "");
  if (cleaned === "") return null;
  const n = parseFloat(cleaned);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export interface VehicleDeductibleFallback {
  comprehensive: number | null;
  collision: number | null;
}

export function isPhysicalDamageInactive(
  coverages: PolicyCoverages | null | undefined,
): boolean {
  const block = coverages?.physicalDamage;
  if (!block || typeof block === "string") return !block;
  return block.status === "removed";
}

// CSR-1383 — derive the policy-level comp/collision deductible pair from the
// `coverages.physicalDamage` block. GEICO-synced vehicles do not carry the
// per-vehicle `deductibleComp` / `deductibleCollision` fields (the Data Center
// GEICO sync keys its per-vehicle breakdown by description, not VIN, and never
// lifts it onto the vehicle records the way the Progressive sync does), so
// `formatVehicleCoiLine` falls back to this block-level pair when a vehicle has
// no per-vehicle deductibles. Returns nulls when the block is absent or empty.
export function getPhysicalDamageFallbackDeductibles(
  coverages: PolicyCoverages | null | undefined,
): VehicleDeductibleFallback {
  const block = coverages?.physicalDamage;
  if (!block || typeof block === "string") return { comprehensive: null, collision: null };
  if (block.status === "removed") return { comprehensive: null, collision: null };
  return {
    comprehensive: parseDeductibleAmount(block.comprehensiveDeductible),
    collision: parseDeductibleAmount(block.collisionDeductible),
  };
}

export interface FormatVehicleCoiLineOptions {
  // CSR-1458 — when true, the vehicle is excluded from the curated Physical
  // Damage selection, so no comp/collision deductible is rendered for it (not
  // the per-vehicle value, not the policy-level fallback). The Year/Make/Model/
  // VIN/Value segments are unaffected.
  suppressPhysicalDamage?: boolean;
}

// Single-line label for a vehicle/trailer in the Description of Operations.
// Returns "" when the entry has no identifying fields (no Y/M/M/VIN); callers
// should drop empty lines from the joined output.
// `fallback` (CSR-1383) supplies policy-level comp/collision deductibles that
// are used only when the vehicle itself carries none (the GEICO shape) — never
// overriding a per-vehicle value.
export function formatVehicleCoiLine(
  v: PolicyVehicle,
  fallback?: VehicleDeductibleFallback,
  options?: FormatVehicleCoiLineOptions,
): string {
  if (isNonOwnedAttachedTrailer(v)) return "Non-Owned Attached Trailer";
  const parts: string[] = [];
  if (v.year !== undefined && v.year !== null && String(v.year).trim() !== "") parts.push(String(v.year));
  if (v.make) parts.push(v.make);
  if (v.model) parts.push(v.model);
  if (v.vin) parts.push(`VIN: ${v.vin}`);
  const value = pickPdNumber(v, "value", "comprehensive", "limits");
  if (value !== null) parts.push(`(${fmtAmount(value)})`);
  if (options?.suppressPhysicalDamage) return parts.join(", ");
  const collDed =
    pickPdNumber(v, "deductibleCollision", "collision", "deductibles") ??
    fallback?.collision ??
    null;
  if (collDed !== null) parts.push(`Deductible (Collision): ${fmtAmount(collDed)}`);
  const compDed =
    pickPdNumber(v, "deductibleComp", "comprehensive", "deductibles") ??
    fallback?.comprehensive ??
    null;
  if (compDed !== null) parts.push(`Deductible (Comprehensive): ${fmtAmount(compDed)}`);
  return parts.join(", ");
}
